/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import { Box, Paper, Group, Image } from '@mantine/core';

// styles
import classes from './brands.module.css';

// components
import { Pagination } from '@components/atoms/Pagination';
import { Datatable } from '@components/organisms/Datatable';
import { Button } from '@components/atoms/Button';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';

// i18n
import { useTranslation } from 'react-i18next';

// icons
import { IconCirclePlus } from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// api
import { useBrandsApi } from '@features/resources/brands/api/get-brands';
import { useRemove } from '@features/resources/brands/api/remove-brand';

// configs
import { tableSchemaBase as columns } from '@features/resources/brands/brands.columns';
import { tableSchemaBase as columnsWithoutContact } from '@features/resources/brands/brandsWithoutContact.columns';

// utils
import {
	convertArrayToLabelValue,
	getVisibleColumns,
} from '@utils/functions';
import { TParsedBrand } from '@features/resources/brands/types';
import { useBrands } from '@features/resources/brands/hooks/useBrands';

import { TTargetColumn } from '@global-types/index';
import { useCountryCodes } from 'shared/api/get-country-codes';
import isNil from 'lodash/isNil';
import {
	IPFS_STATUS_FILTER_OPTIONS,
	VERIFIED_FILTER_OPTIONS,
} from '@utils/options';
import { DrawerBrand } from '@features/resources/brands/components/DrawerBrand';
import { AddBrand } from '@features/resources/brands/components/AddBrand/AddBrand';
import { useOrganizationsHasBrands } from '@api/get-organizations-has-entity-brands';
import { ALL_COUNTRIES } from '@config/constants/all-countries';
import { MenuSelectFilter } from '@components/molecules';
import { EditBrand } from '@features/resources/brands/components/EditBrand';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Brands = () => {
	const { t } = useTranslation();

	const {
		openDrawerEditBrand,
		showScrollFilters,
		openDrawerAddBrand,
		handleAddItem,
		ref,
		brandsDatatableOptions,
		setBrandsDatatableOptions,
		containerRef,
		handleScroll,
		handleOrganizationFilter,
		openDrawer,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		handleOpenConfirmationDeleteItem,
		handleViewMore,
		handleRowClicked,
		handleSelectedRecordsChange,
		handleSortStatusChange,
		isOverflowFilters,
		handlepaginationchangesize,
		handlechangepagination,
		brandsPage,
		brandsPageSize,
		brandsOpenConfirmationModalDelete,
		setBrandsOpenConfirmationModalDelete,
		brandsQueryParams,
		brandsSortStatus,
		brandsSortParams,
		brandsSelectedRow,
		handleCountryFilter,
		handleapplystatusfilter,
		handleapplyipfsstatusfilter,
		handleCloseAddBrandDrawer,
		handleCloseEditBrandDrawer,
		handleCloseShowMoreBrandDrawer,
		handleEditBrandDrawer,
		handleEdit,
		setBrandsWithoutContactDatatableOptions,
		brandsWithoutContactDatatableOptions,
	} = useBrands();

	// APIs
	const { data: organizations } =
		useOrganizationsHasBrands();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useBrandsApi(
		brandsPage,
		brandsPageSize,
		brandsQueryParams,
		brandsSortParams
	);

	const remove = useRemove(brandsPage, brandsPageSize);

	const { data: countryCodes } =
		useCountryCodes('brands');
	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? ALL_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const { checkAcl, checkAccess, checkRole } =
		useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			{/*Drawer add brand */}
			<AddBrand
				opened={openDrawerAddBrand}
				onClose={handleCloseAddBrandDrawer}
			/>

			{/*Drawer edit brand */}
			<EditBrand
				opened={openDrawerEditBrand}
				onClose={handleCloseEditBrandDrawer}
			/>

			{/*Drawer show more brand */}
			<DrawerBrand
				opened={openDrawer}
				handledelete={handleOpenConfirmationDeleteItem}
				onClose={handleCloseShowMoreBrandDrawer}
				data={brandsSelectedRow}
				handleupdate={handleEditBrandDrawer}
			/>

			{/*Delete confirmation modal */}
			<ConfirmationModal
				title={t('brands.alert.confirmationDelete.title')}
				description={t(
					'brands.alert.confirmationDelete.description'
				)}
				onConfirm={() => {
					handleConfirmDeleteItem(remove);
				}}
				onClose={handleCloseConfirmationDeleteItem}
				opened={
					brandsOpenConfirmationModalDelete &&
					brandsOpenConfirmationModalDelete.open
				}
			/>

			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={handleOrganizationFilter}
												label="organizations"
												size="lg"
												options={convertArrayToLabelValue(
													organizations
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.searchOrganization'
												)}
												placeholder={t(
													'filters.placeholders.selectOrganization'
												)}
											/>
										</Box>
									</Authorization>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											value={
												brandsQueryParams[
													'location.countryCode'
												]
													? brandsQueryParams[
															'location.countryCode'
														]['$in']
													: []
											}
											isPersist
											onApply={handleCountryFilter}
											label={t('global.country')}
											size="lg"
											options={filteredCountries}
											placeholderSearch={t(
												'filters.placeholderSearch.searchCountry'
											)}
											placeholder={t(
												'filters.placeholders.selectCountry'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuSelectFilter
											onApply={handleapplystatusfilter}
											label={t('global.status')}
											size="lg"
											options={VERIFIED_FILTER_OPTIONS}
											placeholder={t(
												'filters.placeholders.selectStatus'
											)}
											withoutSearch
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuSelectFilter
											withoutSearch
											onApply={
												handleapplyipfsstatusfilter
											}
											label={t('global.IPFSStatus')}
											size="lg"
											options={IPFS_STATUS_FILTER_OPTIONS}
											placeholder={t(
												'filters.placeholders.selectIPFSStatus'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterNumeric
											}
										>
											<AddFiltersButton>
												{t('global.addFilters')}
											</AddFiltersButton>
										</Box>
									</Authorization>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.BRANDS.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={handleAddItem}
								>
									{t('brands.addBrand')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={handleAddItem}
								>
									{t('brands.addBrand')}
								</Button>
							</div>
						)}
				</Box>

				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? setBrandsDatatableOptions(
										getVisibleColumns(columns, cols)
									)
								: setBrandsWithoutContactDatatableOptions(
										getVisibleColumns(
											columnsWithoutContact,
											cols
										)
									);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? columns
								: columnsWithoutContact
						}
						columns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? brandsDatatableOptions
								: brandsWithoutContactDatatableOptions
						}
						data={data}
						onRowClick={handleRowClicked}
						onViewMore={handleViewMore}
						onEditItem={handleEdit}
						onVerify={() => {}}
						onRemoveItem={(row: TParsedBrand) => {
							setBrandsOpenConfirmationModalDelete({
								open: true,
								params: row,
							});
						}}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={null}
						onSelectedRecordsChange={
							handleSelectedRecordsChange
						}
						onSortStatusChange={handleSortStatusChange}
						sortstatus={brandsSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={brandsPageSize}
					totalitems={totalItems}
					activepage={brandsPage}
					handlechangesize={handlepaginationchangesize}
					onChange={handlechangepagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default Brands;
